import React, { useEffect, useState } from "react";
import { api, resolveAvatar } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import EmployeeHome from "@/pages/EmployeeHome";
import {
  Users, Building2, CalendarClock, UserCheck, TrendingUp, Loader2, Briefcase, IdCard,
} from "lucide-react";
import {
  BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from "recharts";

const COLORS = ["#7a1420", "#e08a1e", "#2f7d5b", "#3a6ea5", "#7d5ba6"];

const StatCard = ({ icon: Icon, label, value, tone, testid }) => (
  <div className="bg-card rounded-xl border border-border p-5 sm:p-6 hover:-translate-y-0.5 transition-transform" data-testid={testid}>
    <div className="flex items-start justify-between">
      <div>
        <p className="text-xs font-semibold uppercase tracking-[0.15em] text-muted-foreground">{label}</p>
        <p className="font-heading text-3xl font-extrabold text-foreground mt-2 font-mono">{value}</p>
      </div>
      <div className={`w-11 h-11 rounded-lg flex items-center justify-center ${tone}`}>
        <Icon className="w-5 h-5" />
      </div>
    </div>
  </div>
);

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);

  useEffect(() => {
    if (user?.role !== "admin") return;
    api.get("/dashboard/stats").then((r) => setStats(r.data)).catch(() => {});
  }, [user]);

  if (user?.role !== "admin") return <EmployeeHome />;

  if (!stats)
    return <div className="flex justify-center py-20"><Loader2 className="w-7 h-7 animate-spin text-primary" /></div>;

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      <div className="flex items-center gap-4 sm:gap-5">
        <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-2xl sm:text-3xl font-bold overflow-hidden border-2 border-accent/40 shadow-sm shrink-0" data-testid="dashboard-user-photo">
          {user?.avatar ? <img src={resolveAvatar(user.avatar)} alt={user.name} className="w-full h-full object-cover" /> : user?.name?.[0]?.toUpperCase()}
        </div>
        <div>
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground ashoka-underline inline-block">Welcome back, {user?.name?.split(" ")[0]}</h2>
          <p className="text-muted-foreground mt-3">{stats.role === "admin" ? "Overview of workforce and departmental activity." : "Here is a summary of your account."}</p>
        </div>
      </div>

      {stats.role !== "admin" ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
          <StatCard icon={Building2} label="My Department" value={stats.department} tone="bg-primary/10 text-primary" testid="stat-my-department" />
          <StatCard icon={Briefcase} label="My Designation" value={stats.designation} tone="bg-accent/15 text-accent" testid="stat-my-designation" />
          <StatCard icon={IdCard} label="Employee Code" value={stats.employee_code} tone="bg-[#3a6ea5]/10 text-[#3a6ea5]" testid="stat-my-code" />
          <StatCard icon={CalendarClock} label="My Pending Leaves" value={stats.my_pending_leaves} tone="bg-destructive/10 text-destructive" testid="stat-my-pending-leaves" />
          <StatCard icon={CalendarClock} label="My Total Leaves" value={stats.my_total_leaves} tone="bg-[#2f7d5b]/10 text-[#2f7d5b]" testid="stat-my-total-leaves" />
          <StatCard icon={UserCheck} label="Attendance Today" value={stats.checked_in_today ? "Marked" : "Not marked"} tone="bg-primary/10 text-primary" testid="stat-my-attendance" />
        </div>
      ) : (
      <>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
        <StatCard icon={Users} label="Total Employees" value={stats.total_employees} tone="bg-primary/10 text-primary" testid="stat-total-employees" />
        <StatCard icon={UserCheck} label="Present Today" value={stats.present_today} tone="bg-[#2f7d5b]/10 text-[#2f7d5b]" testid="stat-present-today" />
        <StatCard icon={Building2} label="Departments" value={stats.departments} tone="bg-accent/15 text-accent" testid="stat-departments" />
        <StatCard icon={CalendarClock} label="Pending Leaves" value={stats.pending_leaves} tone="bg-destructive/10 text-destructive" testid="stat-pending-leaves" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        <div className="lg:col-span-3 bg-card rounded-xl border border-border p-6">
          <h3 className="font-heading text-lg font-semibold text-foreground flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-primary" /> Headcount by Department
          </h3>
          <div className="h-72 mt-4">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={stats.department_breakdown} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                <XAxis dataKey="name" tick={{ fontSize: 11 }} interval={0} angle={-15} textAnchor="end" height={60} />
                <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
                <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid hsl(var(--border))" }} />
                <Bar dataKey="count" fill="#7a1420" radius={[6, 6, 0, 0]} maxBarSize={54} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="lg:col-span-2 bg-card rounded-xl border border-border p-6">
          <h3 className="font-heading text-lg font-semibold text-foreground">Department Share</h3>
          <div className="h-72 mt-4">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={stats.department_breakdown} dataKey="count" nameKey="name" cx="50%" cy="50%" outerRadius={90} innerRadius={50} paddingAngle={2}>
                  {stats.department_breakdown.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid hsl(var(--border))" }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="grid grid-cols-2 gap-2 mt-2">
            {stats.department_breakdown.map((d, i) => (
              <div key={d.name} className="flex items-center gap-2 text-xs">
                <span className="w-3 h-3 rounded-sm" style={{ background: COLORS[i % COLORS.length] }} />
                <span className="text-muted-foreground truncate">{d.name}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
      </>
      )}
    </div>
  );
}
